import { onBeforeUnmount, onMounted, ref } from "vue"

// Потяг вниз в начале списка обновляет экран, как в мобильных приложениях.
// В standalone-режиме PWA браузерного pull-to-refresh нет вообще, а в обычной
// вкладке он срабатывает только от самого верха документа — прокручиваемый
// контейнер приложения до него не доходит.
//
//   const scroller = ref(null)
//   const { pullDistance, refreshing } = usePullToRefresh(scroller)

// Порог срабатывания и потолок, до которого тянется индикатор.
const TRIGGER_DISTANCE = 72
const MAX_DISTANCE = 110
// Горизонтальный жест — это прокрутка календаря, а не обновление.
const HORIZONTAL_SLOP = 12

/** Есть ли между целью касания и контейнером собственная вертикальная прокрутка. */
const hasScrolledAncestor = (target, container) => {
  let node = target instanceof Element ? target : null

  while (node && node !== container) {
    const style = getComputedStyle(node)
    const scrollable =
      /(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight

    if (scrollable && node.scrollTop > 0) return true

    node = node.parentElement
  }

  return false
}

export const usePullToRefresh = (containerRef, onRefresh) => {
  const pullDistance = ref(0)
  const refreshing = ref(false)

  let startY = 0
  let startX = 0
  let tracking = false

  const container = () => containerRef.value

  const reset = () => {
    tracking = false
    pullDistance.value = 0
  }

  const onTouchStart = (event) => {
    if (refreshing.value || event.touches.length !== 1) return

    const node = container()
    if (!node || node.scrollTop > 0) return
    if (hasScrolledAncestor(event.target, node)) return

    tracking = true
    startY = event.touches[0].clientY
    startX = event.touches[0].clientX
  }

  const onTouchMove = (event) => {
    if (!tracking) return

    const deltaY = event.touches[0].clientY - startY
    const deltaX = Math.abs(event.touches[0].clientX - startX)

    if (deltaY <= 0 || deltaX > HORIZONTAL_SLOP) return reset()

    // Тянем с сопротивлением: жест не должен уводить индикатор на пол-экрана.
    pullDistance.value = Math.min(MAX_DISTANCE, deltaY * 0.5)

    // Гасим родную прокрутку, иначе экран уезжает вместе с индикатором.
    if (event.cancelable) event.preventDefault()
  }

  const onTouchEnd = async () => {
    if (!tracking) return

    const triggered = pullDistance.value >= TRIGGER_DISTANCE * 0.5
    reset()
    if (!triggered) return

    refreshing.value = true
    try {
      await onRefresh()
    } finally {
      refreshing.value = false
    }
  }

  onMounted(() => {
    const node = container()
    if (!node) return

    node.addEventListener("touchstart", onTouchStart, { passive: true })
    // passive: false — иначе preventDefault в touchmove браузер игнорирует.
    node.addEventListener("touchmove", onTouchMove, { passive: false })
    node.addEventListener("touchend", onTouchEnd)
    node.addEventListener("touchcancel", reset)
  })

  onBeforeUnmount(() => {
    const node = container()
    if (!node) return

    node.removeEventListener("touchstart", onTouchStart)
    node.removeEventListener("touchmove", onTouchMove)
    node.removeEventListener("touchend", onTouchEnd)
    node.removeEventListener("touchcancel", reset)
  })

  return { pullDistance, refreshing }
}
